if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.injectStyles();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.suppressCartOpen();
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        // ============================================================
        // FREEZE the animation data BEFORE any DOM re-render
        // (renderContents replaces #cart-icon-bubble, so we must
        //  capture these coordinates NOW, not after the render)
        // ============================================================
        const flyData = this.captureFlyData();

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              this.restoreCartOpen();
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              // Show the tick/"Added" state immediately, while the modal
              // (and its button) is still visible to the user.
              this.showAddedState();

              const QUICK_ADD_CLOSE_DELAY = 900; // ms — time to see the tick before closing
              setTimeout(() => {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      CartPerformance.measure("add:paint-updated-sections", () => {
                        this.cart.renderContents(response);
                      });
                      this.animateCartAdd(flyData);
                      // Only now is it safe to let the drawer respond to
                      // future open() calls again.
                      this.restoreCartOpen();
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              }, QUICK_ADD_CLOSE_DELAY);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
              this.animateCartAdd(flyData);
              this.showAddedState();
              // Safe to restore right away — renderContents already ran.
              this.restoreCartOpen();
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
            // Make sure a failed request doesn't leave the drawer suppressed forever.
            this.restoreCartOpen();
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      /* ============================================================
       * Silent cart open suppression
       * ============================================================ */

      suppressCartOpen() {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && !drawer._patchedOpen) {
          drawer._originalOpen = drawer.open;
          drawer.open = function (...args) {
            if (this._suppressOpen) return;
            return this._originalOpen.apply(this, args);
          };
          drawer._patchedOpen = true;
        }
        if (drawer) drawer._suppressOpen = true;

        const notif = document.querySelector('cart-notification');
        if (notif && !notif._patchedOpen) {
          const method = notif.open ? 'open' : 'show';
          notif._originalOpen = notif[method] || function () {};
          notif[method] = function (...args) {
            if (this._suppressOpen) return;
            return this._originalOpen.apply(this, args);
          };
          notif._patchedOpen = true;
        }
        if (notif) notif._suppressOpen = true;
      }

      restoreCartOpen() {
        setTimeout(() => {
          const drawer = document.querySelector('cart-drawer');
          if (drawer) drawer._suppressOpen = false;
          const notif = document.querySelector('cart-notification');
          if (notif) notif._suppressOpen = false;
        }, 700);
      }

      /* ============================================================
       * Fly-to-cart animation
       * ============================================================ */

      captureFlyData() {
        const sourceImage = this.getProductImage();
        if (!sourceImage) return null;

        const sourceRect = sourceImage.getBoundingClientRect();
        if (!sourceRect.width || !sourceRect.height) return null;

        const targetEl = this.getCartTarget();
        if (!targetEl) return null;

        const targetRect = targetEl.getBoundingClientRect();
        if (!targetRect.width && !targetRect.height) return null;

        return {
          src: sourceImage.currentSrc || sourceImage.src,
          sourceRect: {
            left: sourceRect.left,
            top: sourceRect.top,
            width: sourceRect.width,
            height: sourceRect.height,
          },
          targetRect: {
            left: targetRect.left,
            top: targetRect.top,
            width: targetRect.width,
            height: targetRect.height,
          },
        };
      }

      getProductImage() {
        const scope = this.closest('.shopify-section') || this.closest('.product') || document;

        const selectors = [
          '.product__media img',
          '.product__media-list img',
          '.product__media-item img',
          'media-gallery img',
          '.product-single__photo img',
          '.product__image img',
          '.product__media-wrapper img',
          '.product__photo-wrapper img',
          'img',
        ];

        for (const sel of selectors) {
          const img = scope.querySelector(sel);
          if (img && img.offsetWidth > 0 && img.offsetHeight > 0) return img;
        }
        return scope.querySelector('img');
      }

      getCartTarget() {
        const icon =
          document.getElementById('cart-icon-bubble') ||
          document.querySelector('.header__icon--cart') ||
          document.querySelector('a[href$="/cart"]');
        if (!icon) return null;
        return icon.querySelector('.cart-count-bubble') || icon;
      }

      animateCartAdd(flyData) {
        if (!flyData) return;

        // Wait for renderContents to finish painting
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.flyImageToCart(flyData);
            setTimeout(() => this.bumpCartCount(), 80);
          });
        });
      }

      flyImageToCart(flyData) {
        const { src, sourceRect, targetRect } = flyData;
        if (!src) return;

        // Start size: cap it so a huge hero image doesn't fly off-screen
        const startW = Math.min(sourceRect.width, 160);
        const startH = Math.min(sourceRect.height, 160);

        // Start centered on the visible portion of the product image
        const startLeft = sourceRect.left + sourceRect.width / 2 - startW / 2;
        const startTop = sourceRect.top + sourceRect.height / 2 - startH / 2;

        // End centered on cart bubble
        const endW = 22;
        const endH = 22;
        const endLeft = targetRect.left + targetRect.width / 2 - endW / 2;
        const endTop = targetRect.top + targetRect.height / 2 - endH / 2;

        const clone = document.createElement('img');
        clone.src = src;
        clone.className = 'fly-to-cart-clone';
        clone.alt = '';

        Object.assign(clone.style, {
          position: 'fixed',
          left: startLeft + 'px',
          top: startTop + 'px',
          width: startW + 'px',
          height: startH + 'px',
          objectFit: 'cover',
          borderRadius: '8px',
          boxShadow: '0 12px 30px rgba(0,0,0,.3)',
          zIndex: '2147483647',
          pointerEvents: 'none',
          margin: '0',
          padding: '0',
          opacity: '1',
          willChange: 'left, top, width, height, opacity, border-radius',
        });

        document.body.appendChild(clone);

        const duration = 850;

        // Animate left/top/width/height for maximum browser reliability.
        // (Animating position offsets avoids containing-block issues with
        //  position:fixed inside transformed ancestors.)
        const anim = clone.animate(
          [
            {
              left: startLeft + 'px',
              top: startTop + 'px',
              width: startW + 'px',
              height: startH + 'px',
              opacity: 1,
              borderRadius: '8px',
            },
            {
              left: endLeft + 'px',
              top: endTop + 'px',
              width: endW + 'px',
              height: endH + 'px',
              opacity: 0.35,
              borderRadius: '50%',
            },
          ],
          {
            duration,
            easing: 'cubic-bezier(.35,.8,.4,1)',
            fill: 'forwards',
          }
        );

        const cleanup = () => clone.remove();
        anim.onfinish = cleanup;
        anim.oncancel = cleanup;
        // Safety net
        setTimeout(cleanup, duration + 200);
      }

      bumpCartCount() {
        const bubble =
          document.querySelector('#cart-icon-bubble .cart-count-bubble') ||
          document.querySelector('.cart-count-bubble') ||
          document.getElementById('cart-icon-bubble');
        if (!bubble) return;

        bubble.classList.remove('cart-count-bubble--bump');
        void bubble.offsetWidth;
        bubble.classList.add('cart-count-bubble--bump');
        setTimeout(() => bubble.classList.remove('cart-count-bubble--bump'), 600);
      }

      /* ============================================================
       * "Added" state on the submit button — animated draw-in check
       * with a radiating burst, matching the reference screenshot.
       * ============================================================ */

      getAddedIconSVG() {
        return `
<svg class="added-check-icon" viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false">
  <g class="check-burst" stroke="currentColor" stroke-width="1.6" stroke-linecap="round">
    <line x1="12" y1="0.5" x2="12" y2="3.6" />
    <line x1="12" y1="20.4" x2="12" y2="23.5" />
    <line x1="0.5" y1="12" x2="3.6" y2="12" />
    <line x1="20.4" y1="12" x2="23.5" y2="12" />
    <line x1="4.05" y1="4.05" x2="6.25" y2="6.25" />
    <line x1="17.75" y1="17.75" x2="19.95" y2="19.95" />
    <line x1="4.05" y1="19.95" x2="6.25" y2="17.75" />
    <line x1="17.75" y1="6.25" x2="19.95" y2="4.05" />
  </g>
  <path class="check-path" d="M5 12.5 L10 17.3 L19 7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" />
</svg>`.trim();
      }

      showAddedState() {
        if (!this.submitButton) return;
        if (this.error) return;

        // Save the original label the first time we touch it
        if (!this._originalButtonText) {
          this._originalButtonText = this.submitButtonText
            ? this.submitButtonText.textContent
            : 'Add to cart';
        }

        // Build (once) and reuse the icon element
        if (!this._addedIconEl) {
          const wrapper = document.createElement('span');
          wrapper.innerHTML = this.getAddedIconSVG();
          this._addedIconEl = wrapper.firstElementChild;
          this._addedIconEl.classList.add('added-check-icon--hidden');
        }

        if (this._addedIconEl.parentElement !== this.submitButton) {
          if (this.submitButtonText) {
            this.submitButton.insertBefore(this._addedIconEl, this.submitButtonText);
          } else {
            this.submitButton.insertBefore(this._addedIconEl, this.submitButton.firstChild);
          }
        }

        this.submitButton.classList.add('product-form__submit--added');
        if (this.submitButtonText) this.submitButtonText.textContent = 'Added';
        this.submitButton.setAttribute('aria-disabled', 'true');

        // Restart the SVG draw/burst animation every time
        this._addedIconEl.classList.remove('added-check-icon--hidden');
        this._addedIconEl.classList.remove('is-animating');
        void this._addedIconEl.offsetWidth; // force reflow to restart CSS animation
        this._addedIconEl.classList.add('is-animating');

        clearTimeout(this._addedTimer);
        this._addedTimer = setTimeout(() => {
          this.submitButton.classList.remove('product-form__submit--added');
          if (this.submitButtonText) this.submitButtonText.textContent = this._originalButtonText;
          this._addedIconEl.classList.add('added-check-icon--hidden');
          this._addedIconEl.classList.remove('is-animating');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        }, 2000);
      }

      /* ============================================================
       * Styles
       * ============================================================ */

      injectStyles() {
        if (document.getElementById('product-form-fly-styles')) return;
        const style = document.createElement('style');
        style.id = 'product-form-fly-styles';
        style.textContent = `
          @keyframes cartCountBump {
            0%   { transform: scale(1); }
            40%  { transform: scale(1.4); }
            100% { transform: scale(1); }
          }
          .cart-count-bubble--bump {
            animation: cartCountBump 2s cubic-bezier(.2, .8, .2, 1);
          }
          .fly-to-cart-clone {
            pointer-events: none !important;
            transform: none !important;
          }

          button.product-form__submit.product-form__submit--added {
            background-color: #fc0203 !important;
            color: #fff !important;
            border-color: #ffffff !important;
            display: inline-flex !important;
            align-items: center;
            justify-content: center;
            gap: 0.5em;
          }

          /* ---- Animated check icon ---- */
          .product-form__submit .added-check-icon,
          .added-check-icon {
            display: inline-block !important;
            width: 1.15em !important;
            height: 1.15em !important;
            min-width: 1.15em;
            flex: 0 0 auto;
            color: currentColor;
            fill: none;
            overflow: visible !important;
            vertical-align: middle;
          }
          .added-check-icon--hidden {
            display: none !important;
          }

          .added-check-icon .check-path {
            stroke-dasharray: 28;
            stroke-dashoffset: 28;
          }
          .added-check-icon .check-burst line {
            opacity: 0;
            /* view-box (not fill-box) keeps transform-origin reliable for
               stroke-only shapes like <line>, which have no fill area */
            transform-box: view-box;
            transform-origin: 12px 12px;
            transform: scale(0.3);
          }

          .added-check-icon.is-animating .check-path {
            animation: checkDraw 380ms 120ms cubic-bezier(.65,0,.35,1) forwards;
          }
          .added-check-icon.is-animating .check-burst line {
            animation: checkBurst 500ms 260ms cubic-bezier(.2,.8,.2,1) forwards;
          }
          .added-check-icon.is-animating .check-burst line:nth-child(1) { animation-delay: 260ms; }
          .added-check-icon.is-animating .check-burst line:nth-child(2) { animation-delay: 280ms; }
          .added-check-icon.is-animating .check-burst line:nth-child(3) { animation-delay: 260ms; }
          .added-check-icon.is-animating .check-burst line:nth-child(4) { animation-delay: 280ms; }
          .added-check-icon.is-animating .check-burst line:nth-child(5) { animation-delay: 300ms; }
          .added-check-icon.is-animating .check-burst line:nth-child(6) { animation-delay: 300ms; }
          .added-check-icon.is-animating .check-burst line:nth-child(7) { animation-delay: 320ms; }
          .added-check-icon.is-animating .check-burst line:nth-child(8) { animation-delay: 320ms; }

          @keyframes checkDraw {
            to { stroke-dashoffset: 0; }
          }
          @keyframes checkBurst {
            0%   { opacity: 0; transform: scale(0.3); }
            35%  { opacity: 1; }
            100% { opacity: 0; transform: scale(1.6); }
          }
        `;
        document.head.appendChild(style);
      }

      /* ============================================================
       * ORIGINAL METHODS — untouched
       * ============================================================ */

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}